import {
  MatchplayMatch,
  MatchplayHole,
  MatchStatusCalculation,
  MatchplayFormat,
  HoleWinner
} from '../../types/matchplay-v2';

export class MatchplayScoring {
  
  /**
   * Calculate the current match status and display string
   */
  static calculateMatchStatus(match: MatchplayMatch): MatchStatusCalculation {
    // Safety check for undefined holes array
    const holes = Array.isArray(match.holes) ? match.holes : [];
    
    const team1Wins = holes.filter(h => h.holeWinner === 'team1').length;
    const team2Wins = holes.filter(h => h.holeWinner === 'team2').length;
    const holesPlayed = holes.length;
    const holesRemaining = 18 - holesPlayed;
    const lead = team1Wins - team2Wins;
    const absLead = Math.abs(lead);

    // Determine status
    if (holesPlayed === 18) {
      // All holes played
      if (lead === 0) {
        return {
          status: 'completed',
          display: 'AS Final',
          winner: 'tie',
          holesRemaining: 0,
          lead: 0
        };
      } else {
        return {
          status: 'completed',
          display: `${absLead}&0 Final`,
          winner: lead > 0 ? 'team1' : 'team2',
          holesRemaining: 0,
          lead: absLead
        };
      }
    } else if (absLead > holesRemaining) {
      // Mathematically impossible to catch up
      return {
        status: 'completed',
        display: `${absLead}&${holesRemaining} Final`,
        winner: lead > 0 ? 'team1' : 'team2',
        holesRemaining,
        lead: absLead
      };
    } else if (absLead === holesRemaining && holesRemaining > 0) {
      // Dormie situation
      return {
        status: 'dormie',
        display: `${absLead} dormie`,
        holesRemaining,
        lead: absLead
      };
    } else {
      // Match still active
      if (lead === 0) {
        return {
          status: 'active',
          display: holesPlayed === 0 ? 'AS' : `AS thru ${holesPlayed}`,
          holesRemaining,
          lead: 0
        };
      } else {
        return {
          status: 'active',
          display: `${absLead}${lead > 0 ? 'U' : 'D'} thru ${holesPlayed}`,
          holesRemaining,
          lead: absLead
        };
      }
    }
  }

  /**
   * Calculate single team playing score based on format
   */
  static calculateTeamPlayingScore(
    grossScores: number[],
    handicaps: number[],
    holeNumber: number,
    format: MatchplayFormat
  ): number | null {
    // Safety checks
    if (!Array.isArray(grossScores) || !Array.isArray(handicaps)) {
      return null;
    }

    // Calculate net scores for each player
    const netScores = grossScores.map((gross, index) => {
      if (gross <= 0) return null;
      const handicap = handicaps[index] || 0;
      return this.calculateNetScore(gross, handicap, holeNumber);
    }).filter(score => score !== null) as number[];

    if (netScores.length === 0) return null;

    switch (format) {
      case 'singles':
        return netScores[0] || null;
      case 'fourball':
        // Best ball - lowest net score
        return Math.min(...netScores);
      case 'foursomes':
        // Alternate shot - single score
        return netScores[0] || null;
      default:
        return null;
    }
  }

  /**
   * Calculate playing scores based on format (for both teams)
   */
  static calculatePlayingScores(
    format: MatchplayFormat,
    team1GrossScores: number[],
    team2GrossScores: number[]
  ): { team1PlayingScore: number; team2PlayingScore: number; holeWinner: HoleWinner } {
    let team1PlayingScore: number;
    let team2PlayingScore: number;

    switch (format) {
      case 'singles':
        team1PlayingScore = team1GrossScores[0];
        team2PlayingScore = team2GrossScores[0];
        break;
      
      case 'fourball':
        // Best ball - lowest score wins
        team1PlayingScore = Math.min(...team1GrossScores.filter(s => s > 0));
        team2PlayingScore = Math.min(...team2GrossScores.filter(s => s > 0));
        break;
      
      case 'foursomes':
        // Alternate shot - single team score
        team1PlayingScore = team1GrossScores[0];
        team2PlayingScore = team2GrossScores[0];
        break;
      
      default:
        throw new Error(`Unsupported format: ${format}`);
    }

    // Determine hole winner
    let holeWinner: HoleWinner;
    if (team1PlayingScore < team2PlayingScore) {
      holeWinner = 'team1';
    } else if (team2PlayingScore < team1PlayingScore) {
      holeWinner = 'team2';
    } else {
      holeWinner = 'halved';
    }

    return { team1PlayingScore, team2PlayingScore, holeWinner };
  }

  /**
   * Apply handicap strokes to gross scores (for multiple holes)
   */
  static applyHandicapStrokes(
    grossScores: number[],
    handicapStrokes: number[],
    strokeHoles: number[]
  ): number[] {
    // Safety check for arrays
    if (!Array.isArray(grossScores)) {
      console.error('grossScores must be an array');
      return [];
    }
    
    return grossScores.map((gross, index) => {
      const strokes = handicapStrokes[index] || 0;
      // Apply strokes on designated stroke holes
      // This is a simplified version - real implementation would need stroke allocation table
      return Math.max(0, gross - strokes);
    });
  }

  /**
   * Calculate net score for a single hole
   */
  static calculateNetScore(grossScore: number, playerHandicap: number, holeNumber: number): number {
    if (typeof grossScore !== 'number' || grossScore <= 0) {
      return grossScore;
    }
    
    // Simplified handicap calculation for matchplay
    // In real golf, this would use the course's stroke index for each hole
    // For now, we'll use a simple distribution: players get strokes on holes 1-handicap
    const strokesReceived = playerHandicap >= holeNumber ? 1 : 0;
    
    return Math.max(1, grossScore - strokesReceived);
  }

  /**
   * Get match progress percentage
   */
  static getMatchProgress(match: MatchplayMatch): number {
    if (match.status === 'completed') return 100;
    const holes = Array.isArray(match.holes) ? match.holes : [];
    return Math.round((holes.length / 18) * 100);
  }

  /**
   * Get holes until dormie calculation
   */
  static getHolesUntilDormie(match: MatchplayMatch): number | null {
    if (match.status === 'completed' || match.status === 'dormie') return null;
    
    const holes = Array.isArray(match.holes) ? match.holes : [];
    const team1Wins = holes.filter(h => h.holeWinner === 'team1').length;
    const team2Wins = holes.filter(h => h.holeWinner === 'team2').length;
    const holesRemaining = 18 - holes.length;
    const lead = Math.abs(team1Wins - team2Wins);
    
    return holesRemaining - lead;
  }

  /**
   * Format match status for display
   */
  static formatMatchStatus(match: MatchplayMatch): string {
    const status = this.calculateMatchStatus(match);
    return status.display;
  }

  /**
   * Calculate team statistics for a match
   */
  static calculateTeamStats(match: MatchplayMatch) {
    const holes = Array.isArray(match.holes) ? match.holes : [];
    const team1Wins = holes.filter(h => h.holeWinner === 'team1').length;
    const team2Wins = holes.filter(h => h.holeWinner === 'team2').length;
    const halved = holes.filter(h => h.holeWinner === 'halved').length;
    
    return {
      team1: {
        wins: team1Wins,
        percentage: holes.length > 0 ? Math.round((team1Wins / holes.length) * 100) : 0
      },
      team2: {
        wins: team2Wins,
        percentage: holes.length > 0 ? Math.round((team2Wins / holes.length) * 100) : 0
      },
      halved: {
        count: halved,
        percentage: holes.length > 0 ? Math.round((halved / holes.length) * 100) : 0
      }
    };
  }

  /**
   * Get the next hole to be played
   */
  static getNextHole(match: MatchplayMatch): number {
    if (match.status === 'completed') return -1;
    
    const holes = Array.isArray(match.holes) ? match.holes : [];
    const playedHoles = holes.map(h => h.holeNumber).sort((a, b) => a - b);
    
    // Use startingHole if available, otherwise default to hole 1
    const startHole = match.startingHole || 1;
    
    // First, check holes from starting hole to 18
    for (let hole = startHole; hole <= 18; hole++) {
      if (!playedHoles.includes(hole)) {
        return hole;
      }
    }
    
    // If no unplayed holes found from starting hole to 18, 
    // check holes 1 to starting hole - 1 (for shotgun tournaments)
    for (let hole = 1; hole < startHole; hole++) {
      if (!playedHoles.includes(hole)) {
        return hole;
      }
    }
    
    return -1; // All holes played
  }

  /**
   * Validate score input for a format
   */
  static validateScoreInput(
    format: MatchplayFormat,
    team1Scores: number[],
    team2Scores: number[]
  ): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Check score counts based on format
    switch (format) {
      case 'singles':
        if (team1Scores.length !== 1) {
          errors.push('Singles format requires exactly 1 score per team');
        }
        if (team2Scores.length !== 1) {
          errors.push('Singles format requires exactly 1 score per team');
        }
        break;
      
      case 'fourball':
        if (team1Scores.length !== 2) {
          errors.push('Fourball format requires exactly 2 scores per team');
        }
        if (team2Scores.length !== 2) {
          errors.push('Fourball format requires exactly 2 scores per team');
        }
        break;
      
      case 'foursomes':
        if (team1Scores.length !== 1) {
          errors.push('Foursomes format requires exactly 1 score per team');
        }
        if (team2Scores.length !== 1) {
          errors.push('Foursomes format requires exactly 1 score per team');
        }
        break;
    }

    // Check score ranges (golf scores should be reasonable)
    const allScores = [...team1Scores, ...team2Scores];
    for (const score of allScores) {
      if (score < 1 || score > 15) {
        errors.push(`Score ${score} is outside reasonable range (1-15)`);
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Generate match summary for completed matches
   */
  static generateMatchSummary(match: MatchplayMatch): {
    winner: string;
    result: string;
    keyMoments: Array<{ hole: number; description: string }>;
  } {
    const status = this.calculateMatchStatus(match);
    const stats = this.calculateTeamStats(match);
    
    const keyMoments: Array<{ hole: number; description: string }> = [];
    
    // Find turning points and key moments
    let runningLead = 0;
    const holes = Array.isArray(match.holes) ? match.holes : [];
    for (const hole of holes) {
      if (hole.holeWinner === 'team1') runningLead++;
      else if (hole.holeWinner === 'team2') runningLead--;
      
      // Mark lead changes
      if (Math.abs(runningLead) >= 3) {
        const leadingTeam = runningLead > 0 ? 'Team 1' : 'Team 2';
        keyMoments.push({
          hole: hole.holeNumber,
          description: `${leadingTeam} takes ${Math.abs(runningLead)}-hole lead`
        });
      }
    }

    return {
      winner: status.winner === 'team1' ? 'Team 1' : status.winner === 'team2' ? 'Team 2' : 'Tie',
      result: status.display,
      keyMoments
    };
  }
} 